import React from "react";
import { Plus, Star, X } from "react-feather";
import { C } from "../reporting/reportingStyles";
import { errorToast } from "../../shared/Toast";

export const MAX_BRANCH_IMAGES = 5;
const MAX_BYTES = 10 * 1024 * 1024; // matches the API's multer limit
const ACCEPT = ["image/jpeg", "image/png", "image/webp"];

let seq = 0;
const newItem = (file) => ({
  id: `new-${Date.now()}-${seq++}`,
  kind: "new",
  file,
  preview: URL.createObjectURL(file),
});

/** Saved branch image paths → gallery items. */
export const toGalleryItems = (paths) =>
  (paths || []).map((path) => ({
    id: path,
    kind: "existing",
    path,
    preview: process.env.REACT_APP_URL + path,
  }));

/**
 * Ordered gallery for a branch. The first image is the cover, which the API mirrors into
 * `Branch.image` for app builds that only read one image.
 */
const BranchImagesField = ({ items, onChange }) => {
  const remaining = MAX_BRANCH_IMAGES - items.length;

  const handleAdd = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ""; // allow picking the same file again after removing it
    if (!files.length) return;

    const valid = files.filter((f) => {
      if (!ACCEPT.includes(f.type)) {
        errorToast(`${f.name}: use JPG, PNG or WEBP`);
        return false;
      }
      if (f.size > MAX_BYTES) {
        errorToast(`${f.name}: must be 10 MB or smaller`);
        return false;
      }
      return true;
    });
    if (valid.length > remaining) errorToast(`Max ${MAX_BRANCH_IMAGES} images per branch`);
    onChange([...items, ...valid.slice(0, remaining).map(newItem)]);
  };

  const handleRemove = (id) => {
    const item = items.find((i) => i.id === id);
    if (item?.kind === "new") URL.revokeObjectURL(item.preview);
    onChange(items.filter((i) => i.id !== id));
  };

  const handleMakeCover = (id) => {
    const item = items.find((i) => i.id === id);
    onChange([item, ...items.filter((i) => i.id !== id)]);
  };

  const tile = {
    position: "relative",
    width: "100%",
    aspectRatio: "1",
    borderRadius: 10,
    overflow: "hidden",
    background: C.bg,
  };
  const iconBtn = {
    position: "absolute",
    width: 24,
    height: 24,
    borderRadius: "50%",
    border: "none",
    background: "rgba(255,255,255,0.92)",
    boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    padding: 0,
  };

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
        {items.map((item, index) => {
          const isCover = index === 0;
          return (
            <div
              key={item.id}
              style={{ ...tile, border: isCover ? `2px solid ${C.coral}` : `1px solid ${C.border}` }}
            >
              <img src={item.preview} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              {isCover ? (
                <span
                  style={{
                    position: "absolute",
                    left: 6,
                    bottom: 6,
                    background: C.coral,
                    color: C.white,
                    fontSize: 10,
                    fontWeight: 700,
                    padding: "2px 6px",
                    borderRadius: 4,
                  }}
                >
                  Cover
                </span>
              ) : (
                <button
                  type="button"
                  title="Make cover"
                  onClick={() => handleMakeCover(item.id)}
                  style={{ ...iconBtn, left: 6, bottom: 6 }}
                >
                  <Star size={13} color={C.coral} />
                </button>
              )}
              <button
                type="button"
                title="Remove"
                onClick={() => handleRemove(item.id)}
                style={{ ...iconBtn, top: 6, right: 6 }}
              >
                <X size={14} color={C.text} />
              </button>
            </div>
          );
        })}

        {remaining > 0 && (
          <label
            style={{
              ...tile,
              margin: 0,
              border: `2px dashed ${C.border}`,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 4,
              cursor: "pointer",
              color: C.textMed,
              fontSize: 11,
              fontWeight: 600,
            }}
          >
            <Plus size={18} />
            Add
            <input type="file" multiple accept={ACCEPT.join(",")} onChange={handleAdd} style={{ display: "none" }} />
          </label>
        )}
      </div>
      <div style={{ fontSize: 11, color: C.textLight, marginTop: 10 }}>
        First image is the cover. JPG, PNG or WEBP, up to 10 MB.
      </div>
    </div>
  );
};

export default BranchImagesField;
